import { FilterConditionToolbar, TSavedCondition } from "@irohana-dev/components";
import { Box, Stack, Typography } from "@mui/material";
import { FC, useMemo, useRef, useState } from "react";
import { useDeleteSavedCondition, useSavedConditionList } from "@/api/services/main/saved-condition";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { PartnerFilterConditionPanel } from "@/features/partner/components/partner-list/PartnerFilterConditionPanel";


import {
  TPartnerDisplayColumnSetting,
  TPartnerFilterFormExtended,
  defaultPartnerDisplayColumns,
  defaultPartnerFilterValues,
  isEmptyPartnerFilterValue,
  normalizePartnerDateFields,
  normalizePartnerMultiSelectFields,
} from "@/features/partner/components/partner-list/partner-filter-shared";
import { applyColumnSettings } from "@/features/partner/components/partner-list/partner-filter-condition-adapter";
import { PARTNER_DISPLAY_COLUMNS_STORAGE_KEY } from "@/features/partner/constants/common";
import { TPagination } from "@/types/api";
import { showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { filterConditionShareColor } from "@/components/commons/filter-condition/filter-condition-share-color";
import { hasDisplayColumnsChanged } from "@/components/commons/filter-condition/filter-condition-adapter-kit";

type Props = {
  pagination?: TPagination;
  onApplyAdvancedFilters?: (filters: TPartnerFilterFormExtended) => void;
  onApplyDisplaySettings?: (columns: TPartnerDisplayColumnSetting[]) => void;
  appliedConditionName?: string;
  appliedConditionId?: number | null;
  onAppliedConditionNameChange?: (name: string) => void;
  onAppliedConditionIdChange?: (id: number | null) => void;
  currentDisplayColumns?: TPartnerDisplayColumnSetting[] | null;
  currentAdvancedFilters?: TPartnerFilterFormExtended | null;
};


export const PartnerListTop: FC<Props> = ({
  pagination,
  onApplyAdvancedFilters,
  onApplyDisplaySettings,
  appliedConditionName,
  appliedConditionId,
  onAppliedConditionNameChange,
  onAppliedConditionIdChange,
  currentDisplayColumns,
  currentAdvancedFilters,
}) => {
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [filterMode, setFilterMode] = useState<"create" | "edit">("create");
  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const justSavedConditionRef = useRef(false);

  const deleteSavedCondition = useDeleteSavedCondition();
  const { data: savedConditionsData } = useSavedConditionList("partner");

  const handleFilterPopoverClose = () => {
    setFilterAnchorEl(null);
  };

  const handleOpenCreateFilter = () => {
    setFilterMode("create");
    setFilterAnchorEl(document.body);
  };

  const handleOpenEditFilter = () => {
    setFilterMode("edit");
    setFilterAnchorEl(document.body);
  };

  const formatTotal = (total: number = 0) => {
    return total.toLocaleString("en-US");
  };

  const activeFilterCount = useMemo(() => {
    if (!currentAdvancedFilters) return 0;
    const { registrationDateFrom, registrationDateTo, isOverseas, ...rest } = currentAdvancedFilters;
    let count = Object.values(rest).filter((v) => !isEmptyPartnerFilterValue(v)).length;
    if (registrationDateFrom || registrationDateTo) count += 1;
    if (isOverseas) count += 1;
    return count;
  }, [currentAdvancedFilters]);

  const savedConditions = useMemo<TSavedCondition[]>(
    () =>
      (savedConditionsData?.data ?? []).map((item) => ({
        id: Number(item.id),
        name: item.title,
        shareScope: item.shareScope ?? "self",
        isDefault: item.isDefault ?? false,
        badgeColor: item.defaultColor ?? undefined,
      })),
    [savedConditionsData],
  );

  // A condition that stored 項目設定 owns the columns too, so picking it restores them.
  // One that did not keeps whatever the user currently has.
  const applySavedColumnSettings = (picked: { includeColumnSettings?: boolean; columnSettings?: unknown }) => {
    if (!picked.includeColumnSettings || !picked.columnSettings) return;
    const settings = picked.columnSettings as { order?: string[]; hidden?: string[] };
    const base = currentDisplayColumns ?? [...defaultPartnerDisplayColumns];
    const next = applyColumnSettings(base, settings.order ?? [], settings.hidden ?? []);
    onApplyDisplaySettings?.(next);
    localStorage.setItem(PARTNER_DISPLAY_COLUMNS_STORAGE_KEY, JSON.stringify(next));
  };

  const handleSelectCondition = (id: string | number) => {
    const picked = savedConditionsData?.data.find((item) => Number(item.id) === Number(id));
    if (!picked) return;
    onApplyAdvancedFilters?.({
      ...defaultPartnerFilterValues,
      ...normalizePartnerMultiSelectFields(normalizePartnerDateFields(picked.filters ?? {})),
    } as TPartnerFilterFormExtended);
    applySavedColumnSettings(picked);
    onAppliedConditionNameChange?.(picked.title);
    onAppliedConditionIdChange?.(Number(picked.id));
  };

  const handleDeleteAppliedCondition = async () => {
    if (!appliedConditionId) return;
    try {
      await deleteSavedCondition.mutateAsync(appliedConditionId);
      toast.success("条件を削除しました。");
      onApplyAdvancedFilters?.({ ...defaultPartnerFilterValues });
      onAppliedConditionNameChange?.("");
      onAppliedConditionIdChange?.(null);
    } catch (e) {
      showError(e);
    } finally {
      setIsDeleteConfirmOpen(false);
    }
  };

  const handleApplyFiltersFromPanel = (filters: TPartnerFilterFormExtended) => {
    onApplyAdvancedFilters?.(filters);
    if (justSavedConditionRef.current) {
      justSavedConditionRef.current = false;
      return;
    }
    if (filterMode === "create") {
      onAppliedConditionNameChange?.("");
      onAppliedConditionIdChange?.(null);
    }
  };

  const handleConditionSaved = (id: number, title: string) => {
    justSavedConditionRef.current = true;
    onAppliedConditionNameChange?.(title);
    onAppliedConditionIdChange?.(id);
  };

  const appliedCondition =
    filterMode === "edit" && appliedConditionId
      ? savedConditionsData?.data.find((item) => Number(item.id) === Number(appliedConditionId))
      : undefined;

  const handleClearAppliedFilter = () => {
    onApplyAdvancedFilters?.(defaultPartnerFilterValues);
    onAppliedConditionNameChange?.("");
    onAppliedConditionIdChange?.(null);
    onApplyDisplaySettings?.([...defaultPartnerDisplayColumns]);
    localStorage.setItem(PARTNER_DISPLAY_COLUMNS_STORAGE_KEY, JSON.stringify(defaultPartnerDisplayColumns));
    setIsClearConfirmOpen(false);
  };

  return (
    <>
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="body14Semi">
            全 {formatTotal(pagination?.total)} 件中 {Math.min((pagination?.page ?? 1) * (pagination?.perPage ?? 20), pagination?.total ?? 0)}{" "}
            件を表示
          </Typography>

          <FilterConditionToolbar
            onOpenFilter={handleOpenCreateFilter}
            activeCount={
              activeFilterCount || Number(Boolean(appliedConditionId) || hasDisplayColumnsChanged(currentDisplayColumns, defaultPartnerDisplayColumns))
            }
            conditions={savedConditions}
            selectedId={appliedConditionId ?? undefined}
            onSelect={handleSelectCondition}
            onClearSelection={() => setIsClearConfirmOpen(true)}
            onEdit={handleOpenEditFilter}
            onDelete={() => setIsDeleteConfirmOpen(true)}
            shareColor={filterConditionShareColor}
          />
        </Stack>
      </Box>

      <PartnerFilterConditionPanel
        key={`${filterMode}-${appliedConditionId ?? "new"}`}
        mode={filterMode}
        appliedConditionId={filterMode === "edit" ? appliedConditionId : null}
        appliedConditionName={filterMode === "edit" ? appliedConditionName : ""}
        isOpen={Boolean(filterAnchorEl)}
        onClose={handleFilterPopoverClose}
        onApplyFilters={handleApplyFiltersFromPanel}
        onApplyDisplaySettings={onApplyDisplaySettings}
        initialDisplayColumns={currentDisplayColumns || undefined}
        initialFilters={filterMode === "edit" ? currentAdvancedFilters || undefined : defaultPartnerFilterValues}
        appliedConditionShareScope={appliedCondition?.shareScope}
        appliedConditionIsDefault={appliedCondition?.isDefault}
        appliedConditionIncludeColumnSettings={appliedCondition?.includeColumnSettings}
        appliedConditionColumnSettings={appliedCondition?.columnSettings as { order?: string[]; hidden?: string[] } | null | undefined}
        onConditionSaved={handleConditionSaved}
      />
      <ConfirmDeleteModal
        open={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onOk={handleDeleteAppliedCondition}
        message="この条件を削除してもよろしいですか？"
        title=""
      />
      <ConfirmDeleteModal
        open={isClearConfirmOpen}
        onClose={() => setIsClearConfirmOpen(false)}
        onOk={handleClearAppliedFilter}
        message="設定した条件がすべてクリアされますがよろしいですか？"
        title=""
      />
    </>
  );
};
